import React from 'react';
import {
  Check,
  CheckCheck,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { formatPhoneNumber } from '../../../../utils/formatters';
import { linkify } from '../../../../utils/linkify';

/**
 * Normalised message shape that both the contact-side SMS tab and the
 * dashboard SMS thread modal map their backend data into. Keeps the
 * renderer indifferent to which resolver fed it.
 */
export interface SmsChatMessage {
  smsId: string;
  body: string;
  direction: 'inbound' | 'outbound';
  /** Display name above a cluster — falls back to phone/contactFirstName/assistantName in the renderer. */
  fromName?: string | null;
  /** Used for the "from phone XXX" fallback when no name resolves. */
  fromNumber?: string | null;
  sentAt: string;
  deliveredAt?: string | null;
  readAt?: string | null;
}

export interface SmsChatListProps {
  /** Messages in ASC order — oldest at top, newest at bottom. */
  messages: SmsChatMessage[];
  timezone?: string;
  contactFirstName?: string;
  assistantName?: string;
  /** Bubble to scroll the chat to after the messages commit to the DOM
   *  (anchored open from activity feed). Highlighted with a ring. */
  scrollToMessageId?: string | null;
  /** Jump to the newest message on first paint. Modal usage opts in;
   *  the contact tab leaves it off so the embedded thread doesn't yank
   *  the surrounding page down. */
  autoScrollToLatestOnLoad?: boolean;
  /** Backend says there's more history available. Renderer pulls the
   *  next page when the top sentinel scrolls into view. */
  hasOlder?: boolean;
  onLoadOlder?: () => void;
  /** While the older page is in flight — drives the inline spinner at
   *  the top of the thread. */
  isLoadingOlder?: boolean;
  /** Mirror of hasOlder/onLoadOlder for forward pagination — relevant
   *  when the user opens anchored at an older message and wants to
   *  pull additional context past the initial newer bucket. Renderer
   *  auto-loads on bottom-sentinel intersection. */
  hasNewer?: boolean;
  onLoadNewer?: () => void;
  isLoadingNewer?: boolean;
  /** Reset the initial-scroll guard when this key changes (e.g. when
   *  the caller switches contacts). Without it the second open would
   *  inherit the first thread's scroll state. */
  resetKey?: string | null;
}

const fmtTime = (iso: string, tz?: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const fmtDateHeader = (iso: string, tz?: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

/** Full date · time · timezone label rendered on hover/click of a
 *  message bubble. Lets the agent confirm the exact moment a text was
 *  sent without breaking the inline minute-only label. */
const fmtFullTimestamp = (iso: string, tz?: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

/* Group consecutive messages by calendar day in the org's timezone — the
 * thread date dividers and message times all use organization.timezone, so
 * the day-boundary detection here must use the same zone (not UTC) or the
 * divider drops when local-day rolls over but UTC-day hasn't. */
function groupMessages(messages: SmsChatMessage[], tz?: string) {
  const localDateKey = (iso: string): string => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: tz,
      }).formatToParts(new Date(iso));
      const y = parts.find((p) => p.type === 'year')?.value ?? '';
      const m = parts.find((p) => p.type === 'month')?.value ?? '';
      const d = parts.find((p) => p.type === 'day')?.value ?? '';
      return `${y}-${m}-${d}`;
    } catch {
      return iso.slice(0, 10);
    }
  };

  const groups: { dateLabel: string; items: SmsChatMessage[] }[] = [];
  let currentDate = '';
  for (const m of messages) {
    const date = localDateKey(m.sentAt);
    if (date !== currentDate) {
      groups.push({ dateLabel: m.sentAt, items: [m] });
      currentDate = date;
    } else {
      groups[groups.length - 1].items.push(m);
    }
  }
  return groups;
}

/**
 * Presentational chat list. Owns nothing about WHERE the messages came
 * from — the caller (SmsTab, SmsThreadModal) wires its data hook and
 * passes a normalised `SmsChatMessage[]` plus optional behavioural
 * flags (anchor scroll, autoscroll-to-latest, load-older pagination).
 *
 * All chat UX lives here in one place:
 *  - day dividers
 *  - per-cluster sender label + direction arrow
 *  - bubble styling (inbound muted / outbound blue)
 *  - timestamp: minute inline; full date+time+tz on hover (native title)
 *    and on click (toggles inline label to the full form)
 *  - status checkmarks on outbound (single / double / read)
 *  - "See latest messages" floating pill when newest scrolled out
 *  - "Load older messages" button at the top + position preservation
 *  - scroll-to-message after data commits (anchored open)
 */
export const SmsChatList: React.FC<SmsChatListProps> = ({
  messages,
  timezone,
  contactFirstName,
  assistantName,
  scrollToMessageId,
  autoScrollToLatestOnLoad = false,
  hasOlder = false,
  onLoadOlder,
  isLoadingOlder = false,
  hasNewer = false,
  onLoadNewer,
  isLoadingNewer = false,
  resetKey,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [activeStampId, setActiveStampId] = React.useState<string | null>(null);

  const groups = React.useMemo(() => groupMessages(messages, timezone), [messages, timezone]);

  // ── Initial scroll ─────────────────────────────────────────────
  // Pin the scroll position on first render: autoScrollToLatestOnLoad
  // controls bottom-vs-top. We use a double rAF so the bubbles have
  // actually measured before we set scrollTop — on short viewports a
  // single tick fires while scrollHeight is still 0 and the jump
  // silently no-ops.
  const didInitialScroll = React.useRef(false);
  React.useEffect(() => {
    didInitialScroll.current = false;
  }, [resetKey]);
  React.useLayoutEffect(() => {
    if (didInitialScroll.current || messages.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        el.scrollTop = autoScrollToLatestOnLoad ? el.scrollHeight : 0;
        didInitialScroll.current = true;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [messages.length, autoScrollToLatestOnLoad]);

  // ── Load-older position preservation ──────────────────────────
  // Capture scrollHeight just before the older page renders; after
  // it lands shift scrollTop by the delta so the message the user
  // was looking at stays put rather than jumping to the new top.
  const anchorHeightRef = React.useRef<number | null>(null);
  React.useLayoutEffect(() => {
    if (anchorHeightRef.current == null) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop += el.scrollHeight - anchorHeightRef.current;
    anchorHeightRef.current = null;
  }, [messages.length]);

  // ── Pagination ────────────────────────────────────────────────
  // Two parallel paths fire the same loaders:
  //  1. Click the pill at top/bottom — required on tall viewports
  //     (2xl+) where the small initial window fits without producing a
  //     scrollable surface. No scroll = no scroll-edge trigger, so we
  //     need an explicit affordance.
  //  2. Scroll-edge detection — natural infinite scroll on smaller
  //     screens where the content overflows. Direction inferred from
  //     scrollTop delta so we never fire the opposite side by mistake.
  const loadOlderRef = React.useRef(onLoadOlder);
  const loadNewerRef = React.useRef(onLoadNewer);
  loadOlderRef.current = onLoadOlder;
  loadNewerRef.current = onLoadNewer;
  const isLoadingOlderRef = React.useRef(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;
  const isLoadingNewerRef = React.useRef(isLoadingNewer);
  isLoadingNewerRef.current = isLoadingNewer;
  const hasOlderRef = React.useRef(hasOlder);
  hasOlderRef.current = hasOlder;
  const hasNewerRef = React.useRef(hasNewer);
  hasNewerRef.current = hasNewer;
  // Lifecycle latch — locked while we have a fetch in flight, released
  // by an effect watching `isLoading*`. Prevents double-fire when a
  // scroll handler runs faster than React's state has updated.
  const firingOlderRef = React.useRef(false);
  const firingNewerRef = React.useRef(false);
  React.useEffect(() => {
    if (!isLoadingOlder) firingOlderRef.current = false;
  }, [isLoadingOlder]);
  React.useEffect(() => {
    if (!isLoadingNewer) firingNewerRef.current = false;
  }, [isLoadingNewer]);
  // Set right before a load fires; the very next scroll event (which
  // comes from the post-load scrollTop adjustment in the prepend layout
  // effect) clears the flag and skips itself — without this, the
  // adjustment registered as a downward scroll and could fire the
  // newer loader right after older landed.
  const suppressNextScrollRef = React.useRef(false);

  const fireLoadOlder = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoadingOlderRef.current || firingOlderRef.current) return;
    if (!hasOlderRef.current) return;
    firingOlderRef.current = true;
    anchorHeightRef.current = el.scrollHeight;
    suppressNextScrollRef.current = true;
    loadOlderRef.current?.();
  }, []);

  const fireLoadNewer = React.useCallback(() => {
    if (isLoadingNewerRef.current || firingNewerRef.current) return;
    if (!hasNewerRef.current) return;
    firingNewerRef.current = true;
    suppressNextScrollRef.current = true;
    loadNewerRef.current?.();
  }, []);

  React.useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    let prevScrollTop = root.scrollTop;
    // Brief grace period after mount so the init scroll-to-anchor (and
    // the auto-scroll-to-latest in plain mode) don't trip the edge
    // check before the user has done anything.
    let ready = false;
    const readyTimer = setTimeout(() => {
      ready = true;
      prevScrollTop = root.scrollTop;
    }, 250);

    const onScroll = () => {
      const cur = root.scrollTop;
      const delta = cur - prevScrollTop;
      prevScrollTop = cur;
      if (suppressNextScrollRef.current) {
        suppressNextScrollRef.current = false;
        return;
      }
      if (!ready) return;
      if (delta === 0) return;
      const fromTop = cur;
      const fromBottom = root.scrollHeight - cur - root.clientHeight;
      if (delta < 0 && fromTop <= 120) fireLoadOlder();
      else if (delta > 0 && fromBottom <= 120) fireLoadNewer();
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(readyTimer);
      root.removeEventListener('scroll', onScroll);
    };
  }, [fireLoadOlder, fireLoadNewer]);

  // ── New-message appearance ────────────────────────────────────
  // Diff the rendered message-id set against the previous render. IDs
  // present now but not last time are flagged briefly so the bubble
  // picks up `.animate-sms-appear` — a soft fade + lift instead of
  // popping in. Initial mount and resetKey changes seed the ref
  // without animating so the entire opening thread doesn't ripple.
  const prevIdsRef = React.useRef<Set<string> | null>(null);
  // Map id → cascade index within its batch so the bubbles fade in
  // staggered (60ms each) rather than all popping at once. This is what
  // makes "load older" feel like a stream rather than a chunk landing.
  const [appearOrder, setAppearOrder] = React.useState<Map<string, number>>(new Map());
  React.useEffect(() => {
    prevIdsRef.current = null;
    setAppearOrder(new Map());
  }, [resetKey]);
  React.useEffect(() => {
    const currentIds = new Set(messages.map((m) => m.smsId));
    // Treat null prev (mount), empty prev (hold-until-loaded pattern in
    // SmsThreadModal), or current empty (transient) as seed — the
    // hold-until-loaded jump from [] to ~11 messages would otherwise
    // animate the entire opening thread.
    if (
      prevIdsRef.current === null ||
      prevIdsRef.current.size === 0 ||
      currentIds.size === 0
    ) {
      prevIdsRef.current = currentIds;
      return;
    }
    const added: string[] = [];
    currentIds.forEach((id) => {
      if (!prevIdsRef.current!.has(id)) added.push(id);
    });
    prevIdsRef.current = currentIds;
    if (added.length === 0) return;
    setAppearOrder((prev) => {
      const next = new Map(prev);
      added.forEach((id, i) => next.set(id, i));
      return next;
    });
    // Hold just past the 220ms fade so React stops applying the class.
    const timer = setTimeout(() => {
      setAppearOrder((prev) => {
        const next = new Map(prev);
        added.forEach((id) => next.delete(id));
        return next;
      });
    }, 320);
    return () => clearTimeout(timer);
  }, [messages]);

  // ── "See latest" pill ─────────────────────────────────────────
  // Sentinel at the bottom of the thread; we listen to scroll events
  // on every scrollable ancestor (modal body, contact tab body, etc.)
  // so the pill flips regardless of which container is actually doing
  // the scrolling.
  const bottomSentinelRef = React.useRef<HTMLDivElement>(null);
  const [showJumpLatest, setShowJumpLatest] = React.useState(false);
  React.useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    if (!sentinel || messages.length === 0) return;

    const check = () => {
      const r = sentinel.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const atLatest = r.top <= viewportH + 60;
      setShowJumpLatest(!atLatest);
    };

    const ancestors: (HTMLElement | Window)[] = [window];
    let parent: HTMLElement | null = sentinel.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) {
        ancestors.push(parent);
      }
      parent = parent.parentElement;
    }

    ancestors.forEach((a) =>
      a.addEventListener('scroll', check, { passive: true } as AddEventListenerOptions),
    );
    window.addEventListener('resize', check);
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(check);
    });

    return () => {
      cancelAnimationFrame(raf1);
      ancestors.forEach((a) => a.removeEventListener('scroll', check));
      window.removeEventListener('resize', check);
    };
  }, [messages.length]);

  const jumpToLatest = () => {
    // Scroll ONLY the inner thread container — never call
    // `scrollIntoView` here, since that propagates to every scrollable
    // ancestor and yanks the whole page down on short viewports.
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  // ── Anchored scroll-to-message ────────────────────────────────
  // After messages commit, walk the scrollable container's children
  // and scroll the matching bubble into the middle of the viewport.
  // didScrollFor guards against re-jumping when background refetches
  // re-render the list.
  const didScrollFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    didScrollFor.current = null;
  }, [resetKey, scrollToMessageId]);
  React.useEffect(() => {
    if (!scrollToMessageId || messages.length === 0) return;
    if (didScrollFor.current === scrollToMessageId) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(
      `[data-msg-id="${CSS.escape(scrollToMessageId)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'auto', block: 'center' });
    didScrollFor.current = scrollToMessageId;
  }, [scrollToMessageId, messages.length]);

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      {showJumpLatest && (
        <button
          type="button"
          onClick={jumpToLatest}
          className="hidden md:inline-flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-medium bg-foreground text-background shadow-lg hover:opacity-90 transition-opacity"
        >
          <ArrowDown className="h-3.5 w-3.5" />
          See latest messages
        </button>
      )}

      <div
        ref={scrollRef}
        style={{ overflowAnchor: 'none' }}
        className="flex-1 min-h-0 overflow-y-auto scrollbar-custom pr-1 pb-2"
      >
        {hasOlder && onLoadOlder && (
          <div className="flex justify-center pb-2 pt-2">
            {isLoadingOlder ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/70 text-foreground/80 text-[11px] font-medium shadow-sm">
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '180ms' }}
                />
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '360ms' }}
                />
              </span>
            ) : (
              <button
                type="button"
                onClick={fireLoadOlder}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/70 hover:bg-muted text-foreground/80 hover:text-foreground text-[11px] font-medium shadow-sm transition-colors cursor-pointer"
              >
                <ChevronUp className="h-3.5 w-3.5" />
                Load older messages
              </button>
            )}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {groups.map((group, gIdx) => (
            <div key={gIdx} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-center my-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-full">
                  {fmtDateHeader(group.dateLabel, timezone)}
                </span>
              </div>

              {group.items.map((m, i) => {
                const isOut = m.direction === 'outbound';
                const prev = group.items[i - 1];
                const startsCluster = !prev || prev.direction !== m.direction;
                const isAnchor = !!scrollToMessageId && m.smsId === scrollToMessageId;
                const appearIdx = appearOrder.get(m.smsId);
                const isAppearing = appearIdx !== undefined;

                return (
                  <div
                    key={m.smsId}
                    data-msg-id={m.smsId}
                    className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex flex-col max-w-[78%] sm:max-w-[70%] ${
                        isOut ? 'items-end' : 'items-start'
                      }`}
                    >
                      {startsCluster && (
                        <div
                          className={`flex items-center gap-1 text-[10px] font-medium text-muted-foreground mb-0.5 px-1 ${
                            isOut ? 'flex-row-reverse text-right' : 'text-left'
                          }`}
                        >
                          {isOut ? (
                            <ArrowUpRight className="h-3 w-3 call-color-outbound flex-shrink-0" />
                          ) : (
                            <ArrowDownLeft className="h-3 w-3 call-color-inbound flex-shrink-0" />
                          )}
                          <span className="truncate">
                            {isOut
                              ? m.fromName || assistantName
                              : m.fromName ||
                                contactFirstName ||
                                (m.fromNumber ? formatPhoneNumber(m.fromNumber) : '')}
                          </span>
                        </div>
                      )}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveStampId((p) => (p === m.smsId ? null : m.smsId));
                        }}
                        title={fmtFullTimestamp(m.sentAt, timezone)}
                        style={undefined}
                        className={`px-3 py-2 rounded-2xl text-[13px] leading-snug whitespace-pre-wrap break-words shadow-sm cursor-pointer ${
                          isOut
                            ? 'bg-blue-500 text-white rounded-br-md dark:bg-blue-600'
                            : 'bg-muted text-foreground rounded-bl-md dark:bg-foreground/10'
                        } ${
                          isAnchor
                            ? 'ring-2 ring-primaryfortext/60 ring-offset-2 ring-offset-background'
                            : ''
                        } ${isAppearing ? 'animate-sms-appear' : ''}`}
                      >
                        {linkify(m.body)}
                      </div>
                      <div
                        className={`flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground px-1 ${
                          isOut ? 'flex-row-reverse' : ''
                        }`}
                      >
                        <span>
                          {activeStampId === m.smsId
                            ? fmtFullTimestamp(m.sentAt, timezone)
                            : fmtTime(m.sentAt, timezone)}
                        </span>
                        {isOut && (
                          <span className="inline-flex items-center text-muted-foreground/80">
                            {m.readAt ? (
                              <CheckCheck className="h-3 w-3 text-blue-500" />
                            ) : m.deliveredAt ? (
                              <CheckCheck className="h-3 w-3" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {hasNewer && onLoadNewer && (
          <div className="flex justify-center pt-2 pb-2">
            {isLoadingNewer ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/70 text-foreground/80 text-[11px] font-medium shadow-sm">
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '180ms' }}
                />
                <span
                  className="animate-dot-pulse inline-block h-1 w-1 rounded-full bg-foreground/70"
                  style={{ animationDelay: '360ms' }}
                />
              </span>
            ) : (
              <button
                type="button"
                onClick={fireLoadNewer}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/70 hover:bg-muted text-foreground/80 hover:text-foreground text-[11px] font-medium shadow-sm transition-colors cursor-pointer"
              >
                <ChevronDown className="h-3.5 w-3.5" />
                Load newer messages
              </button>
            )}
          </div>
        )}
        <div ref={bottomSentinelRef} aria-hidden className="h-px w-full" />
      </div>
    </div>
  );
};

export default SmsChatList;
